/**
 * Database schema definitions for Fluentry
 * Using Drizzle ORM with Turso (LibSQL/SQLite)
 *
 * Includes standard tables for Better Auth
 */

import { sql } from "drizzle-orm";
import { integer, sqliteTable, text, index, uniqueIndex } from "drizzle-orm/sqlite-core";

// ------------------------------------------------------------------------------
// Better Auth Tables
// ------------------------------------------------------------------------------

export const user = sqliteTable("user", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  email: text("email").notNull().unique(),
  emailVerified: integer("email_verified", { mode: "boolean" }).notNull(),
  image: text("image"),
  createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
  updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),

  // Custom Fluentry preferences
  targetLanguage: text("target_language").default("en"),
  interfaceLanguage: text("interface_language").default("en"),
  voiceEnabled: integer("voice_enabled", { mode: "boolean" }).default(true),
  accountStatus: text("account_status", { enum: ["active", "suspended"] }).notNull().default("active"),
});

export const session = sqliteTable("session", {
  id: text("id").primaryKey(),
  expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
  token: text("token").notNull().unique(),
  createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
  updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  userId: text("user_id")
    .notNull()
    .references(() => user.id),
});

export const account = sqliteTable("account", {
  id: text("id").primaryKey(),
  accountId: text("account_id").notNull(),
  providerId: text("provider_id").notNull(),
  userId: text("user_id")
    .notNull()
    .references(() => user.id),
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  idToken: text("id_token"),
  accessTokenExpiresAt: integer("access_token_expires_at", { mode: "timestamp" }),
  refreshTokenExpiresAt: integer("refresh_token_expires_at", { mode: "timestamp" }),
  scope: text("scope"),
  password: text("password"),
  createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
  updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
});

export const verification = sqliteTable("verification", {
  id: text("id").primaryKey(),
  identifier: text("identifier").notNull(),
  value: text("value").notNull(),
  expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
  createdAt: integer("created_at", { mode: "timestamp" }),
  updatedAt: integer("updated_at", { mode: "timestamp" }),
});

export const adminMembers = sqliteTable(
  "admin_members",
  {
    id: text("id").primaryKey(),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    role: text("role", {
      enum: ["admin_owner", "admin_operator", "admin_support", "admin_analyst", "admin_readonly"],
    })
      .notNull()
      .default("admin_readonly"),
    createdBy: text("created_by"),
    createdAt: integer("created_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
    updatedAt: integer("updated_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
  },
  (table) => ({
    userUniqueIdx: uniqueIndex("admin_members_user_uq").on(table.userId),
    roleIdx: index("admin_members_role_idx").on(table.role),
  }),
);

export const organizations = sqliteTable(
  "organizations",
  {
    id: text("id").primaryKey(),
    name: text("name").notNull(),
    organizationType: text("organization_type", { enum: ["general", "education", "enterprise"] })
      .notNull()
      .default("general"),
    status: text("status", { enum: ["active", "suspended"] }).notNull().default("active"),
    createdBy: text("created_by")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    createdAt: integer("created_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
    updatedAt: integer("updated_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
  },
  (table) => ({
    nameUniqueIdx: uniqueIndex("organizations_name_uq").on(table.name),
    nameIdx: index("organizations_name_idx").on(table.name),
    statusIdx: index("organizations_status_idx").on(table.status),
  }),
);

export const organizationMembers = sqliteTable(
  "organization_members",
  {
    id: text("id").primaryKey(),
    organizationId: text("organization_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    role: text("role", { enum: ["org_owner", "org_admin", "org_member", "org_viewer"] })
      .notNull()
      .default("org_member"),
    status: text("status", { enum: ["active", "invited", "removed"] }).notNull().default("active"),
    createdAt: integer("created_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
    updatedAt: integer("updated_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
  },
  (table) => ({
    organizationIdx: index("organization_members_org_idx").on(table.organizationId),
    userIdx: index("organization_members_user_idx").on(table.userId),
    roleIdx: index("organization_members_role_idx").on(table.role),
    organizationUserUniqueIdx: uniqueIndex("organization_members_org_user_uq").on(
      table.organizationId,
      table.userId,
    ),
  }),
);

// ------------------------------------------------------------------------------
// Fluentry Business Logic Tables
// ------------------------------------------------------------------------------

/**
 * Conversions table - stores translation/conversion history
 */
export const conversions = sqliteTable(
  "conversions",
  {
    id: text("id").primaryKey(),
    userId: text("user_id").references(() => user.id, { onDelete: "cascade" }),
    subjectType: text("subject_type", { enum: ["user", "guest"] }).notNull().default("user"),
    subjectId: text("subject_id").notNull(),
    input: text("input").notNull(),
    inputHash: text("input_hash").notNull(),
    rawTextRef: text("raw_text_ref"),
    targetLanguage: text("target_language").notNull(),
    casualOutput: text("casual_output").notNull(),
    formalOutput: text("formal_output").notNull(),
    conversationalOutput: text("conversational_output").notNull(),
    detectedLanguages: text("detected_languages", { mode: "json" }).$type<string[]>(),
    confidence: integer("confidence"), // 0-100
    processingTime: integer("processing_time"), // milliseconds
    createdAt: integer("created_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
  },
  (table) => ({
    userIdIdx: index("user_id_idx").on(table.userId),
    userIdCreatedAtIdx: index("user_id_created_at_idx").on(table.userId, table.createdAt),
    subjectIdx: index("conversions_subject_idx").on(table.subjectType, table.subjectId),
  }),
);

/**
 * Voice recordings metadata table
 */
export const voiceRecordings = sqliteTable(
  "voice_recordings",
  {
    id: text("id").primaryKey(),
    userId: text("user_id").references(() => user.id, { onDelete: "cascade" }),
    subjectType: text("subject_type", { enum: ["user", "guest"] }).notNull().default("user"),
    subjectId: text("subject_id").notNull(),
    storageKey: text("storage_key").notNull(),
    transcript: text("transcript"),
    duration: integer("duration").notNull(), // seconds
    status: text("status", { enum: ["processing", "completed", "failed"] }).default("processing"),
    createdAt: integer("created_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
  },
  (table) => ({
    userIdIdx: index("voice_user_id_idx").on(table.userId),
    statusIdx: index("voice_status_idx").on(table.status),
    subjectIdx: index("voice_subject_idx").on(table.subjectType, table.subjectId),
  }),
);

/**
 * Editor assist history table
 */
export const editorAssists = sqliteTable(
  "editor_assists",
  {
    id: text("id").primaryKey(),
    userId: text("user_id"),
    subjectType: text("subject_type", { enum: ["user", "guest"] }).notNull().default("user"),
    subjectId: text("subject_id").notNull(),
    command: text("command").notNull(),
    input: text("input").notNull(),
    inputHash: text("input_hash").notNull(),
    rawTextRef: text("raw_text_ref"),
    targetLanguage: text("target_language").notNull(),
    nativeLanguage: text("native_language").notNull(),
    selectionStart: integer("selection_start"),
    selectionEnd: integer("selection_end"),
    tone: text("tone"),
    length: text("length"),
    maxAlternatives: integer("max_alternatives"),
    alternatives: text("alternatives", { mode: "json" }).$type<
      Array<{ text: string; confidence: number; nativeExplanation: string }>
    >(),
    transport: text("transport", { enum: ["http", "ws"] }).notNull().default("http"),
    requestId: text("request_id"),
    createdAt: integer("created_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
  },
  (table) => ({
    userIdIdx: index("editor_assists_user_id_idx").on(table.userId),
    createdAtIdx: index("editor_assists_created_at_idx").on(table.createdAt),
    subjectIdx: index("editor_assists_subject_idx").on(table.subjectType, table.subjectId),
  }),
);

export const privacyPreferences = sqliteTable(
  "privacy_preferences",
  {
    id: text("id").primaryKey(),
    subjectType: text("subject_type", { enum: ["user", "guest"] }).notNull(),
    subjectId: text("subject_id").notNull(),
    trainingOptOut: integer("training_opt_out", { mode: "boolean" }).notNull().default(false),
    updatedAt: integer("updated_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
  },
  (table) => ({
    subjectUniqueIdx: uniqueIndex("privacy_preferences_subject_uq").on(
      table.subjectType,
      table.subjectId,
    ),
  }),
);

export const privacyJobs = sqliteTable(
  "privacy_jobs",
  {
    id: text("id").primaryKey(),
    jobType: text("job_type", { enum: ["export", "delete"] }).notNull(),
    subjectType: text("subject_type", { enum: ["user", "guest"] }).notNull(),
    subjectId: text("subject_id").notNull(),
    scope: text("scope", { enum: ["all", "history", "voice", "training"] }).notNull(),
    status: text("status", {
      enum: ["queued", "processing", "completed", "failed", "expired"],
    })
      .notNull()
      .default("queued"),
    idempotencyKey: text("idempotency_key").notNull(),
    resultR2Key: text("result_r2_key"),
    errorCode: text("error_code"),
    errorMessage: text("error_message"),
    requestedAt: integer("requested_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
    completedAt: integer("completed_at", { mode: "timestamp" }),
    expiresAt: integer("expires_at", { mode: "timestamp" }),
  },
  (table) => ({
    subjectStatusIdx: index("privacy_jobs_subject_status_idx").on(
      table.subjectType,
      table.subjectId,
      table.status,
    ),
    idempotencyUniqueIdx: uniqueIndex("privacy_jobs_idempotency_uq").on(
      table.subjectType,
      table.subjectId,
      table.jobType,
      table.idempotencyKey,
    ),
  }),
);

export const contentAuditEvents = sqliteTable(
  "content_audit_events",
  {
    id: text("id").primaryKey(),
    actorType: text("actor_type", { enum: ["system", "user", "admin"] }).notNull(),
    actorId: text("actor_id").notNull(),
    action: text("action").notNull(),
    purposeCode: text("purpose_code"),
    ticketId: text("ticket_id"),
    createdAt: integer("created_at", { mode: "timestamp" }).default(sql`(unixepoch())`),
  },
  (table) => ({
    actorIdx: index("content_audit_events_actor_idx").on(table.actorType, table.actorId),
    actionIdx: index("content_audit_events_action_idx").on(table.action),
  }),
);

// Type exports
export type User = typeof user.$inferSelect;
export type NewUser = typeof user.$inferInsert;
export type Conversion = typeof conversions.$inferSelect;
export type VoiceRecording = typeof voiceRecordings.$inferSelect;
export type EditorAssist = typeof editorAssists.$inferSelect;
export type PrivacyPreference = typeof privacyPreferences.$inferSelect;
export type PrivacyJob = typeof privacyJobs.$inferSelect;
export type ContentAuditEvent = typeof contentAuditEvents.$inferSelect;
export type AdminMember = typeof adminMembers.$inferSelect;
export type Organization = typeof organizations.$inferSelect;
export type OrganizationMember = typeof organizationMembers.$inferSelect;
